import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Casino as CasinoIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  EmojiEvents as TrophyIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { aviatorAPI } from '../../../services/api';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    style={{ height: '100%' }}
  >
    <Card
      sx={{
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `1px solid ${color}30`,
        borderRadius: 3,
        height: '100%',
        minHeight: 140,
      }}
    >
      <CardContent sx={{ height: '100%' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
          <Box flex={1}>
            <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ color, fontWeight: 'bold', mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: color, opacity: 0.8 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const AviatorStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await aviatorAPI.getStatistics(params);
      if (response.data.success) {
        setStats(response.data.statistics);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchStatistics();
  };

  const handleClearFilters = () => {
    setFilters({
      date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0],
    });
    setTimeout(() => {
      fetchStatistics();
    }, 100);
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 2 }} />
            Aviator Statistics
          </Typography>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Date Range Filter
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleApplyFilters}
                    fullWidth
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    fullWidth
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {stats && (
          <>
            {/* Overview Statistics */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Overview
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Rounds"
                  value={stats.overview?.total_rounds || 0}
                  icon={<CasinoIcon sx={{ fontSize: 40 }} />}
                  color="#6366f1"
                  subtitle="Completed rounds"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Bets"
                  value={stats.overview?.total_bets || 0}
                  icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
                  color="#10b981"
                  subtitle={`Avg ${(stats.overview?.avg_bets_per_round || 0).toFixed(1)} per round`}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Players"
                  value={stats.overview?.unique_players || 0}
                  icon={<PeopleIcon sx={{ fontSize: 40 }} />}
                  color="#f59e0b"
                  subtitle="Unique players"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Cashout Rate"
                  value={formatPercentage(stats.overview?.cashout_percentage)}
                  icon={<TrophyIcon sx={{ fontSize: 40 }} />}
                  color="#8b5cf6"
                  subtitle={`${stats.overview?.total_cashouts || 0} cashouts`}
                />
              </Grid>
            </Grid>

            {/* Financial Statistics */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Financial Summary
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Bet Amount"
                  value={formatCurrency(stats.financial?.total_bet_amount)}
                  icon={<MoneyIcon sx={{ fontSize: 40 }} />}
                  color="#06b6d4"
                  subtitle="Total wagered"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Payouts"
                  value={formatCurrency(stats.financial?.total_payout_amount)}
                  icon={<TrendingDownIcon sx={{ fontSize: 40 }} />}
                  color="#ef4444"
                  subtitle="Paid to players"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="House Profit"
                  value={formatCurrency(stats.financial?.house_profit)}
                  icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
                  color={parseFloat(stats.financial?.house_profit || 0) >= 0 ? '#10b981' : '#ef4444'}
                  subtitle={`${formatPercentage(stats.financial?.house_edge_actual)} edge`}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Average Bet"
                  value={formatCurrency(stats.financial?.avg_bet_amount)}
                  icon={<AssessmentIcon sx={{ fontSize: 40 }} />}
                  color="#8b5cf6"
                  subtitle="Per bet average"
                />
              </Grid>
            </Grid>

            {/* Game Statistics */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Game Performance
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Average Crash Point
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#6366f1', fontWeight: 'bold' }}>
                      {parseFloat(stats.game_stats?.avg_crash_point || 0).toFixed(2)}x
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Highest Crash Point
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                      {parseFloat(stats.game_stats?.max_crash_point || 0).toFixed(2)}x
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Round #{stats.game_stats?.max_crash_round || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Lowest Crash Point
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 'bold' }}>
                      {parseFloat(stats.game_stats?.min_crash_point || 0).toFixed(2)}x
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Round #{stats.game_stats?.min_crash_round || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Average Cashout
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {parseFloat(stats.game_stats?.avg_cashout_multiplier || 0).toFixed(2)}x
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Highest Cashout
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                      {parseFloat(stats.game_stats?.max_cashout_multiplier || 0).toFixed(2)}x
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Instant Crashes
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 'bold' }}>
                      {stats.game_stats?.instant_crash_count || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatPercentage(stats.game_stats?.instant_crash_percentage)} of rounds
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </motion.div>
  );
};

export default AviatorStatistics;
